import React, { useEffect, useState, useRef } from "react";
import api from "../utils/api";

const LiveLogs = () => {
  const [logs, setLogs] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const logsRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('pstu_diary_token');
    
    if (!token) {
      setError("No authentication token found. Please log in.");
      return;
    }

    // Use fetch with proper headers for authentication instead of EventSource
    const connectToLogs = async () => {
      try {
        setError("Connecting to log stream...");
        
        const response = await fetch(`${api.defaults.baseURL}/admin/logs/stream`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache'
          }
        });

        if (response.ok) {
          setError(null);
          setIsConnected(true);
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          
          const readStream = async () => {
            try {
              while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                
                const chunk = decoder.decode(value);
                const lines = chunk.split('\n');
                
                lines.forEach(line => {
                  if (line.startsWith('data: ')) {
                    const data = line.slice(6);
                    if (data.trim()) {
                      try {
                        const logData = JSON.parse(data);
                        const timestamp = new Date().toLocaleTimeString();
                        const logEntry = `[${timestamp}] ${logData.message || logData}\n`;
                        
                        setLogs((prev) => {
                          const newLogs = prev + logEntry;
                          // Keep only last 1000 lines to prevent memory issues
                          const lines = newLogs.split('\n');
                          if (lines.length > 1000) {
                            return lines.slice(-1000).join('\n');
                          }
                          return newLogs;
                        });
                      } catch (err) {
                        // If not JSON, treat as plain text
                        const timestamp = new Date().toLocaleTimeString();
                        const logEntry = `[${timestamp}] ${data}\n`;
                        
                        setLogs((prev) => {
                          const newLogs = prev + logEntry;
                          const lines = newLogs.split('\n');
                          if (lines.length > 1000) {
                            return lines.slice(-1000).join('\n');
                          }
                          return newLogs;
                        });
                      }
                      
                      // Auto-scroll to bottom
                      if (logsRef.current) {
                        logsRef.current.scrollTop = logsRef.current.scrollHeight;
                      }
                    }
                  }
                });
              }
            } catch (err) {
              console.error('Stream reading error:', err);
              setError('Stream reading error: ' + err.message);
              setIsConnected(false);
            }
          };
          
          readStream();
        } else {
          setError(`Connection failed: ${response.status} ${response.statusText}`);
          setIsConnected(false);
        }
      } catch (err) {
        console.error('Connection error:', err);
        setError('Connection error: ' + err.message);
        setIsConnected(false);
      }
    };

    connectToLogs();

    return () => {
      setIsConnected(false);
    };
  }, []);

  const clearLogs = () => {
    setLogs("");
  };

  const downloadLogs = () => {
    const blob = new Blob([logs], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `server-logs-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center", 
        marginBottom: "20px",
        padding: "16px",
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
      }}>
        <h2 style={{ margin: 0, color: "#2d3748" }}>Live Server Logs</h2>
        <div style={{ display: "flex", gap: "10px" }}>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "8px",
            padding: "8px 12px",
            backgroundColor: isConnected ? "#f0fff4" : "#fed7d7",
            color: isConnected ? "#22543d" : "#742a2a",
            borderRadius: "6px",
            fontSize: "14px"
          }}>
            <span style={{ 
              width: "8px", 
              height: "8px", 
              borderRadius: "50%", 
              backgroundColor: isConnected ? "#38a169" : "#e53e3e" 
            }}></span>
            {isConnected ? "Connected" : "Disconnected"}
          </div>
          <button
            onClick={clearLogs}
            style={{
              padding: "8px 16px",
              backgroundColor: "#e2e8f0",
              color: "#4a5568",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px"
            }}
          >
            Clear Logs
          </button>
          <button
            onClick={downloadLogs}
            style={{
              padding: "8px 16px",
              backgroundColor: "#667eea",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px"
            }}
          >
            Download Logs
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          padding: "12px",
          backgroundColor: "#fed7d7",
          color: "#742a2a",
          borderRadius: "6px",
          marginBottom: "16px",
          border: "1px solid #feb2b2"
        }}>
          {error}
        </div>
      )}

      <div
        ref={logsRef}
        style={{
          backgroundColor: "#1e1e1e",
          color: "#f1f1f1",
          fontFamily: "monospace",
          padding: "16px",
          height: "70vh",
          overflowY: "auto",
          border: "1px solid #555",
          borderRadius: "8px",
          whiteSpace: "pre-wrap",
          fontSize: "13px",
          lineHeight: "1.4"
        }}
      >
        {logs || "Connecting to server logs..."}
      </div>

      <div style={{
        marginTop: "16px",
        padding: "12px",
        backgroundColor: "#f7fafc",
        borderRadius: "6px",
        fontSize: "12px",
        color: "#718096"
      }}>
        <strong>Info:</strong> Logs are automatically updated in real-time using JWT authentication. Use the buttons above to clear or download the current log stream.
      </div>
    </div>
  );
};

export default LiveLogs;
